import CloseIcon from "@mui/icons-material/Close";
import { Backdrop, CircularProgress, DialogActions, Divider, FormHelperText, Grid, IconButton } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { AxiosError } from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as constants from "../../constants";
import CustomAutocomplete from "../../shared/AutoComplete";
import ContainedButton from "../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../shared/Button/customOutlinedButton";
import { RootState } from "../../store/store";
import { getActualDetailsTODownload } from "../fiscalUpgrade/services/Financials/actuals.service";
import { StyledInputLabel, SubSectionTitle } from "../fiscalUpgrade/StyledComponents/Container";
import CommonUtilities from "../fiscalUpgrade/Utils/CommonUtilities";
import { notifyUser } from "../fiscalUpgrade/Utils/notifyUser";

type PopUpProps = {
    open: boolean;
    forecastYear: string;
    participantId: number;
    handleClose: () => void;
    saveChanges: (filterData: {
        baiCode: string;
        timeFrame: string;
        monthEndDate: string | null;
        fromDate: string | null;
        toDate: string | null;
        amountMin: string | null;
        amountMax: string | null;
        checkMin: string | null;
        checkMax: string | null;
        amountAll: string | null;
    }) => void; // <-- saveChanges expects filterData argument
};

export default function DownloadPopUpActuals(props: PopUpProps) {
    const { open, forecastYear, participantId, handleClose, saveChanges, } = props;
    const [period, setPeriod] = useState(CommonUtilities.periodOptions.find((p) => p.value === constants.PERIOD_YEARLY) || null);
    const [month, setMonth] = useState<{ id: number; value: string; name: string } | null>(null);
    const [year, setYear] = useState("");
    const [quarter, setQuarter] = useState<{ id: number; value: string; name: string } | null>(null);
    const [semiannually, setSemiannually] = useState<{ id: number; value: string; name: string } | null>(null);
    const [yearlyType, setYearlyType] = useState<{ id: number; value: string; name: string } | null>(null);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const userDetails = useSelector((state: RootState) => state.auth);

    const [yearError, setYearError] = useState(false);
    const [loadingFlag, setLoadingFlag] = useState(false);
    const [periodError, setPeriodError] = useState(false);
    const [monthError, setMonthError] = useState(false);
    const [quarterError, setQuarterError] = useState(false);
    const [semiannuallyError, setSemiannuallyError] = useState(false);
    const [yearlyTypeError, setYearlyTypeError] = useState(false);
    const months = CommonUtilities.monthsOptions;
    const monthOrder = CommonUtilities.monthOrderList;
    const monthOrderOptions = CommonUtilities.monthsOrderListOptions;
    const semiannuallys = CommonUtilities.semiannuallyOptions;
    const yearlyTypes = CommonUtilities.yearlyTypeOptions;
    const quarters = CommonUtilities.quarterOptions;
    const periods = CommonUtilities.periodOptions;
    const baiCodeOptions = [
        { name: "All Types", code: "All Types" },
        { name: "All Deposits", code: "CR DET" },
        { name: "All Withdrawals", code: "DB DET" },
    ];
    const timeFrameOptions = [
        { name: "All Available Dates", code: "All Available Dates" },
        { name: "Month", code: "Month" },
        { name: "Custom Date Range", code: "Custom Date Range" },
    ];
    const [selectedBaiCode, setSelectedBaiCode] = useState(baiCodeOptions[0]); // Default to "All Types"
    const [selectedTimeFrameCode, setSelectedTimeFrameCode] = useState(timeFrameOptions[0]);// Default to "All Available Dates"



    const handleDialogClose = () => {
        handleClose();
        setMonth(null);
        setQuarter(null);
        setSemiannually(null);
        setMonthError(false);
        setPeriodError(false);
        setQuarterError(false);
        setSemiannuallyError(false);
        const defaultPeriod = CommonUtilities.periodOptions.find((p) => p.value === constants.PERIOD_YEARLY);
        setPeriod(defaultPeriod || null);
    };

    const validateBeforeDownload = () => {
        let shouldDisable = false;
        const errors = {
            yearError: false,
            periodError: false,
            monthError: false,
            quarterError: false,
            semiannuallyError: false,
            yearlyTypeError: false
        };
        if (!period?.value) {
            shouldDisable = true;
            errors.periodError = true;
        }

        switch (period?.value) {
            case constants.PERIOD_MONTHLY:
                if (!month || !month.value) {
                    shouldDisable = true;
                    errors.monthError = true;
                }
                break;

            case constants.PERIOD_QUARTERLY:
                if (!quarter || !quarter.value) {
                    shouldDisable = true;
                    errors.quarterError = true;
                }
                break;

            case constants.PERIOD_SEMIANNUALLY:
                if (!semiannually || !semiannually.value) {
                    shouldDisable = true;
                    errors.semiannuallyError = true;
                }
                break;

            case constants.PERIOD_YEARLY:
                shouldDisable = false;
                errors.yearError = false;

                break;
            default:
                break;
        }

        setPeriodError(errors.periodError);
        setYearError(errors.yearError);
        setMonthError(errors.monthError);
        setQuarterError(errors.quarterError);
        setSemiannuallyError(errors.semiannuallyError);
        setYearlyTypeError(errors.yearlyTypeError);
        return !shouldDisable;
    };


    async function toDownload() {
        const isValid = validateBeforeDownload();
        if (!isValid) return;
        setLoadingFlag(true);
        try {
            let subPeriod = "";
            if (period?.value === constants.PERIOD_SEMIANNUALLY) {
                subPeriod = semiannually?.value ?? "";
            } else if (period?.value === constants.PERIOD_QUARTERLY) {
                subPeriod = quarter?.value ?? "";
            } else if (period?.value === constants.PERIOD_MONTHLY) {
                subPeriod = month?.value ?? "";
            }

            const downloadVO = {
                financialYear: forecastYear,
                participantId: participantId,
                period: period?.value ?? "",
                subPeriod: subPeriod,
                username: userDetails.emailId,
            };

            const responseList = await getActualDetailsTODownload(downloadVO);

            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                const base64Data = responseList.data.data.documentContent;
                if (!base64Data) {
                    notifyUser(responseList?.data.message, "error", translate("download"));
                    return;
                }
                const byteCharacters = atob(base64Data);
                const byteArrays = [];

                for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                    const slice = byteCharacters.slice(offset, offset + 512);
                    const byteNumbers = new Array(slice.length);

                    for (let i = 0; i < slice.length; i++) {
                        byteNumbers[i] = slice.charCodeAt(i);
                    }

                    const byteArray = new Uint8Array(byteNumbers);
                    byteArrays.push(byteArray);
                }

                const blob = new Blob(byteArrays, { type: "application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = responseList.data.data.filename || "download";
                document.body.appendChild(a);
                a.click();
                URL.revokeObjectURL(url);
                document.body.removeChild(a);
                setLoadingFlag(false);
                handleDialogClose();
                notifyUser(responseList?.data.message, "success", translate("download"));
            } else {
                setLoadingFlag(false);
                notifyUser(responseList?.data.message, "error", translate("download"));
            }

        } catch (error) {
            if (error instanceof AxiosError) {
                notifyUser(error.response?.data || error.message, "error", translate("download"));
            } else {
                notifyUser("Unexpected Error:", "error", translate("download"));
            }
        } finally {
            setLoadingFlag(false);
            setMonth(null);
            setQuarter(null);
            setSemiannually(null);
            setSemiannually(null);
            const defaultPeriod = CommonUtilities.periodOptions.find((p) => p.value === constants.PERIOD_YEARLY);
            setPeriod(defaultPeriod || null);
        }
    }

    return (
        <Dialog
            open={open}
            fullWidth
        >
            <DialogTitle sx={{
                backgroundColor: "#059ca9",
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '10px 10px'
            }}>
                {translate('download')}
                <IconButton onClick={handleDialogClose} sx={{ color: "white", padding: '0px' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Dialog Content */}
            <DialogContent style={{ paddingTop: '20px' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} >
                        <StyledInputLabel shrink>
                            {translate('period')} <span style={{ color: 'red' }}>*</span>
                        </StyledInputLabel>
                        <CustomAutocomplete
                            size='small'
                            options={periods}
                            value={period}
                            onChange={(event, newValue) => {
                                setPeriod(newValue);
                                setPeriodError(false);
                            }}
                            getOptionLabel={(option) => option.name}
                            placeholder={translate('period')}
                            error={periodError}
                        />
                        {periodError && (
                            <FormHelperText error>{translate('pleaseSelectaValidPeriod')}</FormHelperText>
                        )}
                    </Grid>
                    {period?.value === constants.PERIOD_MONTHLY && (
                        <Grid item xs={12} >
                            <StyledInputLabel shrink>
                                {translate('months')} <span style={{ color: 'red' }}>*</span>
                            </StyledInputLabel>
                            <CustomAutocomplete
                                size='small'
                                options={months}
                                value={month}
                                onChange={(event, newValue) => {
                                    setMonth(newValue)
                                    setMonthError(false);
                                }}
                                getOptionLabel={(option) => option.name}
                                placeholder={translate('month')}
                                error={monthError}
                            />
                            {monthError && (
                                <FormHelperText error>{translate('pleaseSelectaValidMonth')}</FormHelperText>
                            )}
                        </Grid>
                    )}
                    {period?.value === constants.PERIOD_QUARTERLY && (
                        <Grid item xs={12} >
                            <StyledInputLabel shrink>
                                {translate('quarter')} <span style={{ color: 'red' }}>*</span>
                            </StyledInputLabel>
                            <CustomAutocomplete
                                size='small'
                                options={quarters}
                                value={quarter}
                                onChange={(event, newValue) => {
                                    setQuarter(newValue)
                                    setQuarterError(false);
                                }}
                                getOptionLabel={(option) => option.name}
                                placeholder={translate('quarter')}
                                error={quarterError}
                            />
                            {quarterError && (
                                <FormHelperText error>{translate('pleaseSelectaValidQuarter')} </FormHelperText>
                            )}
                        </Grid>
                    )}
                    {period?.value === constants.PERIOD_SEMIANNUALLY && (
                        <Grid item xs={12} >
                            <StyledInputLabel shrink>
                                {translate('semiAnnually')}<span style={{ color: 'red' }}>*</span>
                            </StyledInputLabel>
                            <CustomAutocomplete
                                size='small'
                                options={semiannuallys}
                                value={semiannually}
                                onChange={(event, newValue) => {
                                    setSemiannually(newValue)
                                    setSemiannuallyError(false);
                                }}
                                getOptionLabel={(option) => option.name}
                                placeholder={translate('semiAnnually')}
                                error={semiannuallyError}
                            />
                            {semiannuallyError && (
                                <FormHelperText error>{translate('pleaseSelectaValidSemiannually')}</FormHelperText>
                            )}
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <Divider />
            <DialogActions>
                <ContainedButton onClick={toDownload}> {translate('download')}  </ContainedButton>
                <OutlinedButton onClick={handleDialogClose}  > {translate('cancel')}  </OutlinedButton>
            </DialogActions>
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: "flex",
                    flexDirection: "column",
                }}
                open={loadingFlag}
            >
                <CircularProgress color="inherit" />
                <SubSectionTitle  >{translate('pleasewait')}</SubSectionTitle>
            </Backdrop>
        </Dialog>
    );
}
